import { useLocation, useNavigate } from 'react-router-dom'
import liens from '../../enum/liens'
import { ListeAvisResponseModel } from '../listeAvis/types/filtresListeAvisModel'
import { fontFamilyBlackerDisplay, fontFamilyMonserrat, gecinaBlueMain, gecinaBrownMain, WHITE } from '../../theme'
import { Box, Grid } from '@mui/material'
import { filtresListeAvisResponseEnum } from '../listeAvis/enums/listeAvisEnum'
import React, { useContext, useEffect, useState } from 'react'
import { creationTaxesFoncieres, getAvisDetail } from './api/traitementAvisAPI'
import { detailAvisFormEnum, detailAvisPostEnum, detailAvisResponseEnum, immeubleExterneEnum } from './enums/detailAvisEnum'
import { DetailAvisResponseModel, INITAL_TRAITEMENT_AVIS, TRAITEMENT_IMMEUBLE_VIDE, TraitementAvisModel } from './types/detailAvisModel'
import { FormProvider, useForm } from 'react-hook-form'
import CustomTextField from '../../components/fields/CustomTextField'
import thousandsSeparator from '../../utils/thousandsSeparator'
import { calculerTotalFrais, mapperAvisPourTraitement } from './utils/traitementAvisUtils'
import FormNumberFormatField from '../../components/fields/FormNumberFormatField'
import CustomCheckbox from '../../components/checkbox/CustomCheckbox'
import BlocImmeuble from './components/BlocImmeuble'
import Button from '../../components/button/Button'
import { buttonTypesEnum } from '../../components/button/enums/buttonTypesEnum'
import TableauTotaux from './components/TableauTotaux'
import { displaySnackbar } from '../../components/snackBar/reducer/actions'
import { useDispatch } from 'react-redux'
import { ERREUR_ID } from '../../components/snackBar/SnackBar'
import { TraitementAvisContext } from './context/TraitementAvisContext'
import Loader from '../../components/loader/Loader'
import BandeauAvisATraiter from './components/BandeauAvisATraiter'
import BandeauAvisTraitee from './components/BandeauAvisTraitee'


interface DetailAvisState {
	avis: ListeAvisResponseModel
	avisDetail: DetailAvisResponseModel
	consultationTaxes: boolean
}

const TraitementAvis = () => {
	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { formValues, setFormValues } = useContext(TraitementAvisContext)
	const [loading, setLoading] = useState<boolean>(true)
	const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
	const methods = useForm<TraitementAvisModel>({
		defaultValues: formValues
	})
	const { register, handleSubmit, watch, setValue, getValues } = methods

	const avis = location.state
		? (location.state as DetailAvisState).avis
		: null

	const avisDetail = location.state
		? (location.state as DetailAvisState).avisDetail
		: null

	const consultationTaxes = location.state
		? (location.state as DetailAvisState).consultationTaxes
		: false

	useEffect(() => {
		setFormValues(INITAL_TRAITEMENT_AVIS)
		// @ts-ignore
		setValue(detailAvisFormEnum.montantAPayer, '0')
		// @ts-ignore
		setValue(detailAvisFormEnum.fraisGestion, '0')
		if (!!avis) {
			getAvisDetail({ [detailAvisPostEnum.fichier]: avis![filtresListeAvisResponseEnum.fichierDepose], [detailAvisPostEnum.anneeRedition]: avis![filtresListeAvisResponseEnum.anneeReddition] })
				.then((data) => {
					const avisMapper = mapperAvisPourTraitement(data)
					setFormValues(avisMapper)
					setValue(detailAvisResponseEnum.id, avis[filtresListeAvisResponseEnum.id])
					Object.keys(avisMapper).forEach((avis) => setValue(avis as keyof TraitementAvisModel, avisMapper[avis as keyof typeof avisMapper]))
				})
				.catch(() => {
						dispatch(displaySnackbar(
							{
								id: ERREUR_ID,
								message: 'Fichier de l\'avis introuvable',
								open: true,
								hideIcon: false
							})
						)
						navigate(liens.avis, { state: { rafraichir: true } })
					}
				)
				.finally(() => setLoading(false))
		} else if (!!avisDetail && consultationTaxes) {
			const avisMapper = mapperAvisPourTraitement(avisDetail)
			setFormValues(avisMapper)
			setValue(detailAvisResponseEnum.id, avisDetail[detailAvisResponseEnum.id])
			Object.keys(avisMapper).forEach((avis) => setValue(avis as keyof TraitementAvisModel, avisMapper[avis as keyof typeof avisMapper]))
			// INTDEP-67 : en consultation, on remplace les valeurs figées du XML par le recalcul via taux.
			// BDD non modifiée.
			const fraisRecalcule = Math.round(calculerTotalFrais(avisDetail))
			const totalCotisations = parseFloat(avisDetail[detailAvisResponseEnum.montantAPayer] as unknown as string) - parseFloat(avisDetail[detailAvisResponseEnum.fraisGestion] as unknown as string)
			// @ts-ignore
			setValue(detailAvisFormEnum.fraisGestion, fraisRecalcule.toString())
			// @ts-ignore
			setValue(detailAvisFormEnum.montantAPayer, Math.round(totalCotisations + fraisRecalcule).toString())
			setLoading(false)
		} else {
			navigate(liens.avis)
		}
	}, [avis, avisDetail, consultationTaxes])

	if (loading) {
		return <Loader />
	}

	const onSubmit = (value: TraitementAvisModel) => {
		setLoadingSubmit(true)
		let erreurValidation = false
		if (thousandsSeparator(parseFloat(value[detailAvisResponseEnum.fraisGestion]), true) !== thousandsSeparator(parseFloat(value[detailAvisResponseEnum.frais][detailAvisResponseEnum.total] as string), true)) {
			erreurValidation = true
			setLoadingSubmit(false)
			dispatch(displaySnackbar(
				{
					id: ERREUR_ID,
					message: 'Le montant total de frais saisi pour l\'avis ne correspond pas au total global de frais calculé',
					open: true,
					hideIcon: false
				})
			)
		} else if (thousandsSeparator(parseFloat(value[detailAvisResponseEnum.montantAPayer]), true) !== thousandsSeparator(parseFloat(value[detailAvisResponseEnum.totalLigne][detailAvisResponseEnum.total] as string), true)) {
			erreurValidation = true
			setLoadingSubmit(false)
			dispatch(displaySnackbar(
				{
					id: ERREUR_ID,
					message: 'Le montant total saisi pour l\'avis ne correspond pas au total global calculé',
					open: true,
					hideIcon: false
				})
			)
		}
		value[detailAvisResponseEnum.immeubles].forEach((immeuble, index) => {
			if (thousandsSeparator(parseFloat(immeuble[detailAvisResponseEnum.totalCotisation]), true) !== thousandsSeparator(parseFloat(immeuble[detailAvisResponseEnum.communeCotisation][detailAvisResponseEnum.total] as string), true)) {
				erreurValidation = true
				setLoadingSubmit(false)
				dispatch(displaySnackbar(
					{
						id: ERREUR_ID,
						message: 'Le total des cotisations de l\'immeuble ' + watch(`${detailAvisResponseEnum.immeubles}.${index}.${detailAvisResponseEnum.immeubleExterne}.${immeubleExterneEnum.reference}`) + ' ne correspond pas à la somme des cotisations saisies',
						open: true,
						hideIcon: false
					})
				)
			}
		})

		if (!erreurValidation) {
			creationTaxesFoncieres(value)
				.then(() => navigate(liens.avis, { state: { rafraichir: true } }))
				.catch(() => {
						setLoadingSubmit(false)
						dispatch(displaySnackbar(
							{
								id: ERREUR_ID,
								message: 'Le traitement de l\'avis a échoué',
								open: true,
								hideIcon: false
							})
						)
					}
				)
		}
	}

	return (
		<Box>
			{
				!!avis ?
					<BandeauAvisATraiter avis={avis} />
					:
					<BandeauAvisTraitee avis={avisDetail} consultationTaxes={consultationTaxes} />
			}
			<Grid container sx={{ paddingX: '172px' }}>
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid container sx={{ mt: '40px' }}>
							<Box
								sx={{
									color: gecinaBlueMain,
									paddingTop: '42px',
									fontFamily: fontFamilyBlackerDisplay,
									fontSize: 22,
									fontWeight: 900
								}}
							>
								Caractéristiques de l’avis
							</Box>

							<Grid mt="22px" flexDirection="row" display="flex" width="100%" alignItems="center">
								<Box sx={{ width: 150, color: gecinaBlueMain, minHeight: 90 }}>
									<CustomTextField
										sx={{ height: 51, width: 150, backgroundColor: 'transparent !important' }}
										id={detailAvisFormEnum.totalCotisations}
										disabled={consultationTaxes}
										label="Total cotisations"
										inputProps={{
											disabled: true,
											value: thousandsSeparator((watch(detailAvisFormEnum.montantAPayer) as number) - (watch(detailAvisFormEnum.fraisGestion) as number), true)
										}}
									/>
								</Box>
								<Box sx={{ width: 150, ml: '20px', color: gecinaBlueMain, minHeight: 90 }}>
									<FormNumberFormatField
										sx={{ height: 51, width: 150, backgroundColor: WHITE }}
										register={register(detailAvisFormEnum.fraisGestion, {
											required: 'Champ requis'
										})}
										value={watch(detailAvisFormEnum.fraisGestion)}
										// @ts-ignore
										onChange={(value: string) => setValue(detailAvisFormEnum.fraisGestion, value)}
										id={detailAvisFormEnum.fraisGestion}
										label="Frais de gestion *"
										decimalScale={0}
										withBorder
										maxLength={8}
										disabled={consultationTaxes}
									/>
								</Box>
								<Box sx={{ width: 150, ml: '20px', color: gecinaBlueMain, minHeight: 90 }}>
									<FormNumberFormatField
										sx={{ height: 51, width: 150, backgroundColor: WHITE }}
										register={register(detailAvisFormEnum.montantAPayer, {
											required: 'Champ requis'
										})}
										value={watch(detailAvisFormEnum.montantAPayer)}
										// @ts-ignore TODO ts-ignore
										onChange={(value: string) => setValue(detailAvisFormEnum.montantAPayer, value)}
										id={detailAvisFormEnum.montantAPayer}
										label="Montant à payer *"
										decimalScale={0}
										withBorder
										maxLength={8}
										disabled={consultationTaxes}
									/>
								</Box>
								<Box sx={{ width: 150, ml: '20px', color: gecinaBlueMain, minHeight: 90 }}>
									<Grid container>
											<span
												style={{
													width: '100%',
													fontSize: 16,
													marginBottom: 14,
													marginLeft: 0,
													boxSizing: 'border-box',
													textAlign: 'left',
													fontFamily: fontFamilyMonserrat,
													fontWeight: 600
												}}
											>
												Grand Paris
											</span>
										<CustomCheckbox
											checked={watch(detailAvisFormEnum.grandParis)}
											register={register(detailAvisFormEnum.grandParis)}
											onChange={(event) => {
												// @ts-ignore TODO ts-ignore
												setValue(detailAvisFormEnum.grandParis, event.target.checked)
												setFormValues(getValues())
											}}
											disabled={consultationTaxes}
										/>
									</Grid>
								</Box>
							</Grid>

							{
								formValues.immeubles.map((immeuble, index) =>
									<Grid mt="22px" width="100%" alignItems="center" key={`bloc-immeuble-${index}`}>
										<BlocImmeuble immeuble={immeuble} index={index} disabled={consultationTaxes} />
									</Grid>
								)
							}

							<Button
								sx={{
									height: 51,
									p: 0,
									width: 195,
									fontWeight: 600,
									lineHeight: '18.29px',
									mt: '45px'
								}}
								border
								disabled={consultationTaxes}
								bgcolor="transparent"
								color={gecinaBlueMain}
								type={buttonTypesEnum.button}
								onClick={() => {
									setFormValues({
											...formValues,
											[detailAvisResponseEnum.immeubles]: [
												...formValues[detailAvisResponseEnum.immeubles],
												TRAITEMENT_IMMEUBLE_VIDE
											]
										}
									)
									setValue(`${detailAvisResponseEnum.immeubles}.${formValues.immeubles.length}.${detailAvisFormEnum.ajustementFrais}`, '0' as never)
									setValue(`${detailAvisResponseEnum.immeubles}.${formValues.immeubles.length}.${detailAvisResponseEnum.adresse}`, 'Immeuble complémentaire' as never)
								}}
							>
								Ajouter un immeuble
							</Button>

							<TableauTotaux />
							<Grid container item justifyContent="flex-end" sx={{ mb: '20px', mt: '60px', ml: 0 }}>
								<Grid item sx={{ maxWidth: 'max-content', ml: '24px', minWidth: 'fit-content' }}>
									{
										loadingSubmit ?
											<Loader />
											:
											<Button
												sx={{
													height: 51,
													p: 0,
													width: 195,
													fontWeight: 600,
													lineHeight: '18.29px'
												}}
												bgcolor={gecinaBlueMain}
												color={gecinaBrownMain}
												border={false}
												disabled={consultationTaxes || loading}
												type={buttonTypesEnum.submit}
											>
												Valider
											</Button>
									}
								</Grid>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</Grid>
		</Box>
	)
}

export default TraitementAvis